import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Stack,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon,
  School as SchoolIcon,
  Queue as QueueIcon,
  Inbox as InboxIcon,
  EditNote as ComposeIcon,
  AutoAwesome as TemplateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import whatsappApiService from "../services/whatsappApiService";
import MdlSelectMultipleSchool from "../components/dialog/MdlSelectMultipleSchool";
import { usePageHeader } from "../contexts/PageHeaderContext";

const WA_GREEN = "#25D366";
const WA_DARK = "#128C7E";
const WA_SURFACE_HEADER = alpha(WA_GREEN, 0.12);

const CATEGORY_COLORS = {
  Promotion: "primary",
  Service: "info",
  Renewal: "warning",
  Custom: "secondary",
};

const WHATSAPP_GRADIENT_SX = {
  background: `linear-gradient(135deg, ${WA_GREEN} 0%, ${WA_DARK} 100%)`,
  color: "#fff",
  boxShadow: `0 4px 14px ${alpha(WA_GREEN, 0.35)}`,
  "&:hover": {
    background: "linear-gradient(135deg, #128C7E 0%, #075E54 100%)",
    boxShadow: `0 6px 20px ${alpha(WA_GREEN, 0.45)}`,
  },
  "&.Mui-disabled": {
    background: alpha(WA_GREEN, 0.3),
    color: alpha("#fff", 0.7),
  },
};

const NAV_ACTION_TONES = {
  inbox: {
    main: "#1976d2",
    bg: alpha("#1976d2", 0.1),
    border: alpha("#1976d2", 0.28),
    hoverBg: alpha("#1976d2", 0.16),
  },
  queue: {
    main: "#ed6c02",
    bg: alpha("#ed6c02", 0.1),
    border: alpha("#ed6c02", 0.28),
    hoverBg: alpha("#ed6c02", 0.16),
  },
  schedule: {
    main: WA_DARK,
    bg: alpha(WA_GREEN, 0.12),
    border: alpha(WA_GREEN, 0.32),
    hoverBg: alpha(WA_GREEN, 0.2),
  },
};

const NavActionButton = memo(function NavActionButton({ icon, label, onClick, disabled, tone }) {
  const palette = NAV_ACTION_TONES[tone];

  return (
    <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        px: 1.5,
        py: 0.65,
        minHeight: 36,
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: palette.main,
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: `0 1px 3px ${alpha(palette.main, 0.12)}`,
        gap: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: palette.hoverBg,
          borderColor: palette.main,
          boxShadow: `0 4px 14px ${alpha(palette.main, 0.22)}`,
          transform: "translateY(-1px)",
        },
        "&.Mui-disabled": {
          color: alpha(palette.main, 0.45),
          bgcolor: alpha(palette.main, 0.05),
          borderColor: alpha(palette.main, 0.15),
        },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(palette.main, 0.14),
          color: palette.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      {label}
    </Button>
  );
});

function formatSchoolDisplay(school) {
  const schoolCode = school.school_code || school.schoolCode || "";
  const schoolName = school.school_name || school.name || "";
  const adminNumber =
    school.admin_number || school.adminNumber || school.admin_phone || school.phone || "";
  const adminName = school.admin_name || school.contactPerson || "";

  const parts = [];
  if (schoolCode) parts.push(schoolCode);
  if (adminName) parts.push(adminName);
  if (adminNumber) parts.push(adminNumber);

  if (parts.length > 0) {
    return `${schoolName || "N/A"} (${parts.join(" - ")})`;
  }
  return schoolName || "N/A";
}

function prepareSchoolsForQueue(schoolsData) {
  return schoolsData.map((school) => ({
    school_code: school.school_code || school.schoolCode || "",
    school_name: school.school_name || school.name || "",
    contact_number:
      school.admin_number || school.adminNumber || school.admin_phone || school.phone || "",
    admin_name: school.admin_name || school.contactPerson || "",
  }));
}

function buildQueueOptions(selectedTemplate, scheduledAt = null) {
  const options = {};
  if (scheduledAt) {
    options.scheduled_at = scheduledAt;
  }
  if (selectedTemplate?.id) {
    options.template_id = selectedTemplate.id;
    // Meta name + language are resolved from DB columns meta_template_name, template_language
    if (selectedTemplate.metaTemplateName) {
      options.message_type = "template";
    }
  }
  return options;
}

function normalizePhoneNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";

  let normalized = digits;
  if (normalized.length === 11 && normalized.startsWith("0")) {
    normalized = normalized.slice(1);
  }
  if (normalized.length === 10 && /^[6-9]/.test(normalized)) {
    return `91${normalized}`;
  }
  if (normalized.length === 12 && normalized.startsWith("91") && /^91[6-9]/.test(normalized)) {
    return normalized;
  }
  if (normalized.length > 10) {
    const suffix = normalized.slice(-10);
    if (/^[6-9]\d{9}$/.test(suffix)) {
      return `91${suffix}`;
    }
  }
  return "";
}

function parsePhoneNumbersFromText(text) {
  if (!String(text || "").trim()) return [];

  const seen = new Set();
  const result = [];

  const addNumber = (raw) => {
    const normalized = normalizePhoneNumber(raw);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }
  };

  String(text)
    .split(/[,;\n\r\t|]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach(addNumber);

  if (result.length === 0) {
    const matches = String(text).match(/[+]?[\d][\d\s\-().]{8,18}\d/g) || [];
    matches.forEach(addNumber);
  }

  return result;
}

async function parsePhonesFromSpreadsheetFile(file) {
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (ext === "csv" || ext === "txt") {
    const text = await file.text();
    return parsePhoneNumbersFromText(text);
  }

  if (ext === "xlsx" || ext === "xls") {
    const XLSX = await import("xlsx");
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
    return parsePhoneNumbersFromText(csv);
  }

  throw new Error("Unsupported file. Upload CSV or Excel (.xlsx, .xls).");
}

function prepareRecipientsForQueue(recipientMode, schoolsData, phoneNumbers) {
  if (recipientMode === "numbers") {
    return phoneNumbers.map((contact_number) => ({
      school_code: "",
      contact_number,
    }));
  }
  return prepareSchoolsForQueue(schoolsData);
}

function notifyQueueProcessResult(data) {
  if (!data) return;
  const sent = data.sent ?? 0;
  const failed = data.failed ?? 0;
  const processed = data.processed ?? 0;

  if (processed === 0) {
    toast("No pending messages in queue");
    return;
  }
  if (failed === 0) {
    toast.success(`Sent ${sent} message(s) via WhatsApp`);
  } else if (sent === 0) {
    toast.error(`Failed to send ${failed} message(s)`);
  } else {
    toast.success(`Sent ${sent} message(s), ${failed} failed`);
  }
}

const SectionCard = memo(function SectionCard({
  title,
  subtitle,
  icon,
  action,
  children,
  dense,
  headerBg,
  contentBg,
  sx,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: alpha(WA_GREEN, 0.2),
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: contentBg ?? "#fff",
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            px: dense ? 2 : 2.5,
            py: dense ? 1 : 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: dense ? 1 : 2,
            borderBottom: 1,
            borderColor: "divider",
            ...(headerBg ? { bgcolor: headerBg } : {}),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: dense ? 1 : 1.5, minWidth: 0 }}>
            {icon}
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography
                  variant={dense ? "subtitle2" : "subtitle1"}
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, display: "block" }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {action}
        </Box>
      )}
      <Box
        sx={{
          p: dense ? 1.5 : 2.5,
          bgcolor: contentBg ?? "#fff",
        }}
      >
        {children}
      </Box>
    </Paper>
  );
});

const TemplateCard = memo(function TemplateCard({ template, onUse, onCopy, compact }) {
  const categoryColor = CATEGORY_COLORS[template.category] || "default";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        cursor: "pointer",
        border: 1,
        borderColor: "divider",
        borderRadius: 2.5,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: WA_GREEN,
          boxShadow: `0 8px 24px ${alpha(WA_GREEN, 0.12)}`,
        },
      }}
      onClick={() => onUse(template)}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, pr: 1 }}>
          {template.name}
        </Typography>
        <Chip label={template.category} size="small" color={categoryColor} variant="outlined" />
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: compact ? 2 : 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          lineHeight: 1.5,
          minHeight: compact ? 42 : 63,
        }}
      >
        {template.content}
      </Typography>
      {Array.isArray(template.variableNames) && template.variableNames.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {template.variableNames.map((name) => (
            <Chip
              key={name}
              label={`{${name}}`}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.6875rem" }}
            />
          ))}
        </Stack>
      )}
      {template.metaTemplateName ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Meta: {template.metaTemplateName}
          {template.templateLanguage ? ` · ${template.templateLanguage}` : ""}
        </Typography>
      ) : (
        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
          No Meta template name — sends as text only
        </Typography>
      )}
      {!compact && (
        <Box sx={{ display: "flex", gap: 0.5, mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            sx={{ ...WHATSAPP_GRADIENT_SX, textTransform: "none", flex: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              onUse(template);
            }}
          >
            Use template
          </Button>
          <Tooltip title="Copy text">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopy(template.content);
              }}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
});

const SelectedSchoolChip = memo(function SelectedSchoolChip({ school }) {
  return (
    <Chip
      icon={<SchoolIcon sx={{ fontSize: "14px !important" }} />}
      label={formatSchoolDisplay(school)}
      size="small"
      variant="outlined"
      sx={{
        maxWidth: "100%",
        height: "auto",
        py: 0.25,
        "& .MuiChip-label": {
          whiteSpace: "normal",
          lineHeight: 1.3,
          fontSize: "0.75rem",
        },
      }}
    />
  );
});

const PhoneNumberChip = memo(function PhoneNumberChip({ number }) {
  return (
    <Chip
      icon={<PhoneIcon sx={{ fontSize: "14px !important" }} />}
      label={number}
      size="small"
      variant="outlined"
      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
    />
  );
});

function RecipientsPanel({
  recipientMode,
  onRecipientModeChange,
  selectedCount,
  selectedSchoolsData,
  onOpenSelection,
  phoneInputText,
  onPhoneInputChange,
  onPhoneFileImport,
  phoneNumbers,
  phoneFileInputRef,
}) {
  const theme = useTheme();
  const isSchools = recipientMode === "schools";

  const subtitle = isSchools
    ? selectedCount > 0
      ? `${selectedCount} school${selectedCount === 1 ? "" : "s"} selected`
      : null
    : phoneNumbers.length > 0
      ? `${phoneNumbers.length} number${phoneNumbers.length === 1 ? "" : "s"} ready`
      : null;

  return (
    <SectionCard
      dense
      title="Recipients"
      subtitle={subtitle}
      icon={
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            flexShrink: 0,
          }}
        >
          {isSchools ? <GroupsIcon sx={{ fontSize: 18 }} /> : <PhoneIcon sx={{ fontSize: 18 }} />}
        </Box>
      }
      action={
        isSchools ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<SchoolIcon sx={{ fontSize: "16px !important" }} />}
            onClick={onOpenSelection}
            sx={{ textTransform: "none", borderRadius: 1.5, flexShrink: 0, py: 0.25, minHeight: 28 }}
          >
            {selectedCount > 0 ? "Edit" : "Select"}
          </Button>
        ) : (
          <>
            <input
              ref={phoneFileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              hidden
              onChange={onPhoneFileImport}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadFileIcon sx={{ fontSize: "16px !important" }} />}
              onClick={() => phoneFileInputRef.current?.click()}
              sx={{ textTransform: "none", borderRadius: 1.5, flexShrink: 0, py: 0.25, minHeight: 28 }}
            >
              Import Excel
            </Button>
          </>
        )
      }
      headerBg={alpha("#1976d2", 0.1)}
    >
      <ToggleButtonGroup
        value={recipientMode}
        exclusive
        fullWidth
        size="small"
        onChange={(_, value) => value && onRecipientModeChange(value)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="schools" sx={{ textTransform: "none", py: 0.5 }}>
          Selected schools
        </ToggleButton>
        <ToggleButton value="numbers" sx={{ textTransform: "none", py: 0.5 }}>
          Phone numbers
        </ToggleButton>
      </ToggleButtonGroup>

      {isSchools ? (
        selectedCount === 0 ? (
          <Box
            sx={{
              py: 2,
              px: 1.5,
              textAlign: "center",
              borderRadius: 1.5,
              border: "1px dashed",
              borderColor: alpha(theme.palette.divider, 0.8),
            }}
          >
            <SchoolIcon sx={{ fontSize: 28, color: "text.disabled", mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              No schools selected
            </Typography>
            <Button
              size="small"
              sx={{ mt: 0.75, textTransform: "none", minHeight: 28 }}
              onClick={onOpenSelection}
            >
              Choose schools
            </Button>
          </Box>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 180, overflow: "auto", pr: 0.5 }}>
            {selectedSchoolsData.length > 0 ? (
              selectedSchoolsData.map((school, index) => (
                <SelectedSchoolChip
                  key={school.school_id || school.id || index}
                  school={school}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {selectedCount} school(s) selected
              </Typography>
            )}
          </Stack>
        )
      ) : (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            size="small"
            placeholder="Enter numbers comma-separated, e.g. 9876543210, 919876543210, +91 98765 43210"
            value={phoneInputText}
            onChange={(e) => onPhoneInputChange(e.target.value)}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: "0.8125rem" },
            }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Paste numbers or import from Excel/CSV. Supports +91, 91, or 10-digit format.
          </Typography>
          {phoneNumbers.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxHeight: 120, overflow: "auto" }}>
              {phoneNumbers.map((number) => (
                <PhoneNumberChip key={number} number={number} />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No valid numbers yet
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
}

const WhatsAppMessaging = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedSchoolsData, setSelectedSchoolsData] = useState([]);
  const [recipientMode, setRecipientMode] = useState("schools");
  const [phoneInputText, setPhoneInputText] = useState("");
  const phoneFileInputRef = useRef(null);
  const [message, setMessage] = useState("");
  const [templatesExpanded, setTemplatesExpanded] = useState(false);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schoolSelectionDialogOpen, setSchoolSelectionDialogOpen] = useState(false);

  const [scheduledDateTime, setScheduledDateTime] = useState("");

  const [sending, setSending] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const phoneNumbers = useMemo(
    () => parsePhoneNumbersFromText(phoneInputText),
    [phoneInputText]
  );

  const schoolRecipientCount = selectedSchoolsData.length || selectedSchools.length;
  const recipientCount =
    recipientMode === "numbers" ? phoneNumbers.length : schoolRecipientCount;
  const canSend = recipientCount > 0 && message.trim().length > 0;
  const charCount = message.length;
  const charWarning = charCount > 1000;

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const result = await whatsappApiService.getWhatsAppTemplates();
      if (result.success) {
        setTemplates(Array.isArray(result.data) ? result.data : []);
      } else {
        toast.error(result.error || "Failed to load templates");
        setTemplates([]);
      }
    } catch (error) {
      toast.error("Error loading templates: " + error.message);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleUseTemplate = useCallback((template) => {
    setMessage(template.content);
    setSelectedTemplate(template);
    setTemplateDialogOpen(false);
    toast.success("Template applied!");
  }, []);

  const handleCopyTemplate = useCallback((content) => {
    navigator.clipboard.writeText(content);
    toast.success("Template copied!");
  }, []);

  const handleClearAll = useCallback(() => {
    setMessage("");
    setSelectedTemplate(null);
    setSelectedSchools([]);
    setSelectedSchoolsData([]);
    setPhoneInputText("");
  }, []);

  const handlePhoneFileImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const numbers = await parsePhonesFromSpreadsheetFile(file);
      if (numbers.length === 0) {
        toast.error("No valid phone numbers found in file");
        return;
      }
      setPhoneInputText(numbers.join(", "));
      toast.success(`Imported ${numbers.length} number(s)`);
    } catch (error) {
      toast.error(error.message || "Failed to import file");
    }
  }, []);

  const validateRecipients = useCallback(() => {
    if (recipientMode === "numbers") {
      if (phoneNumbers.length === 0) {
        toast.error("Please enter or import at least one valid phone number");
        return false;
      }
      return true;
    }
    if (selectedSchoolsData.length === 0) {
      toast.error("Please select at least one school");
      return false;
    }
    return true;
  }, [recipientMode, phoneNumbers.length, selectedSchoolsData.length]);

  const resetAfterSend = useCallback(() => {
    setMessage("");
    setSelectedTemplate(null);
    setSelectedSchools([]);
    setSelectedSchoolsData([]);
    setPhoneInputText("");
  }, []);

  const handleSchoolSelectChange = useCallback((selected, schoolData) => {
    setSelectedSchools(selected);
    setSelectedSchoolsData(schoolData || []);
  }, []);

  const runQueueProcessor = useCallback(async (estimatedCount = 10) => {
    const maxBatches = Math.max(1, Math.ceil(estimatedCount / 10) + 1);
    const result = await whatsappApiService.processMessageQueue({
      batchSize: 10,
      maxBatches,
      sleepSeconds: 0,
    });

    if (result.success) {
      notifyQueueProcessResult(result.data);
    } else {
      toast.error(result.error || "Failed to process message queue");
    }

    return result;
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim()) {
      toast.error("Please select a template");
      return;
    }
    if (!validateRecipients()) return;

    setSending(true);
    try {
      const schoolsForQueue = prepareRecipientsForQueue(
        recipientMode,
        selectedSchoolsData,
        phoneNumbers
      );
      const result = await whatsappApiService.addMessagesToQueue(
        schoolsForQueue,
        message,
        buildQueueOptions(selectedTemplate)
      );

      if (result.success) {
        const successCount =
          result.data?.success_count || result.data?.total || recipientCount;
        await runQueueProcessor(successCount);
        resetAfterSend();
      } else {
        const detail = Array.isArray(result.errors) && result.errors.length
          ? result.errors.join("; ")
          : result.error;
        toast.error(detail || "Failed to add messages to queue. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to add messages to queue: " + error.message);
    } finally {
      setSending(false);
    }
  }, [
    message,
    selectedTemplate,
    recipientMode,
    selectedSchoolsData,
    phoneNumbers,
    recipientCount,
    runQueueProcessor,
    validateRecipients,
    resetAfterSend,
  ]);

  const handleScheduleMessage = useCallback(async () => {
    if (!scheduledDateTime) {
      toast.error("Please select a date and time");
      return;
    }
    if (!message.trim()) {
      toast.error("Please select a template");
      return;
    }
    if (!validateRecipients()) return;

    try {
      const schoolsForQueue = prepareRecipientsForQueue(
        recipientMode,
        selectedSchoolsData,
        phoneNumbers
      );
      const result = await whatsappApiService.addMessagesToQueue(
        schoolsForQueue,
        message,
        buildQueueOptions(selectedTemplate, scheduledDateTime)
      );

      if (result.success) {
        const scheduledCount =
          result.data?.success_count || result.data?.total || recipientCount;
        toast.success(`Message scheduled for ${scheduledCount} recipient(s)!`);
        setScheduleDialogOpen(false);
        setScheduledDateTime("");
        resetAfterSend();
      } else {
        const detail = Array.isArray(result.errors) && result.errors.length
          ? result.errors.join("; ")
          : result.error;
        toast.error(detail || "Failed to schedule messages. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to schedule messages: " + error.message);
    }
  }, [
    scheduledDateTime,
    message,
    selectedTemplate,
    recipientMode,
    selectedSchoolsData,
    phoneNumbers,
    recipientCount,
    validateRecipients,
    resetAfterSend,
  ]);

  usePageHeader({
    title: "WhatsApp Messaging",
    breadcrumbs: [
      {
        label: "WhatsApp Messaging",
        icon: <WhatsAppIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
      },
    ],
  });

  const renderTemplateGrid = (compact) => {
    if (!loadingTemplates && templates.length === 0) {
      return (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <TemplateIcon sx={{ fontSize: 40, color: alpha(WA_GREEN, 0.4), mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No templates available. Add active templates in the database to use them here.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {templates.map((template) => (
          <Grid item xs={12} sm={6} md={compact ? 12 : 4} key={template.id}>
            <TemplateCard
              template={template}
              onUse={handleUseTemplate}
              onCopy={handleCopyTemplate}
              compact={compact}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card sx={{ bgcolor: "#fff" }}>
          <Box
            className="page-actions-bar"
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2.5,
              py: 1.25,
              minHeight: 52,
              backgroundColor: "var(--color-primary-surface-muted)",
              borderBottom: "1px solid var(--border-color-hover)",
              borderRadius: "var(--radius-md) var(--radius-md) 0 0",
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                WhatsApp Messaging
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {recipientCount > 0
                  ? `${recipientCount} recipient${recipientCount === 1 ? "" : "s"} selected`
                  : "Compose and send messages to schools"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <NavActionButton
                tone="inbox"
                label="Inbox"
                icon={<InboxIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/whatsapp/inbox")}
              />
              <NavActionButton
                tone="queue"
                label="Queue"
                icon={<QueueIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/whatsapp/queue")}
              />
              <NavActionButton
                tone="schedule"
                label="Schedule"
                icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                onClick={() => setScheduleDialogOpen(true)}
                disabled={!canSend}
              />
            </Stack>
          </Box>

          <CardContent sx={{ p: 3, bgcolor: "#fff" }}>
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <SectionCard
              dense
              headerBg={WA_SURFACE_HEADER}
              title="Compose message"
              subtitle="Select a template — message text cannot be edited manually"
              icon={
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(WA_GREEN, 0.12),
                    color: WA_DARK,
                    flexShrink: 0,
                  }}
                >
                  <ComposeIcon sx={{ fontSize: 18 }} />
                </Box>
              }
            >
              <TextField
                fullWidth
                multiline
                minRows={7}
                maxRows={12}
                placeholder="Select a template below or click Use template…"
                value={message}
                InputProps={{ readOnly: true }}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                    py: 0.25,
                    cursor: "default",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "default",
                    color: message.trim() ? "text.primary" : "text.secondary",
                  },
                }}
              />

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TemplateIcon />}
                  onClick={() => setTemplateDialogOpen(true)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Use template
                </Button>
                <Chip
                  label={`${charCount} chars`}
                  size="small"
                  color={charWarning ? "error" : "default"}
                  variant={charWarning ? "filled" : "outlined"}
                />
              </Stack>

              <Divider sx={{ mb: 1.5, mt: 0.5 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearAll}
                  sx={{ textTransform: "none" }}
                >
                  Clear all
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<SendIcon />}
                  onClick={handleSendMessage}
                  disabled={!canSend || sending}
                  sx={{ ...WHATSAPP_GRADIENT_SX, textTransform: "none", px: 3, borderRadius: 2.5 }}
                >
                  {sending ? "Sending…" : `Send to ${recipientCount || 0} recipient${recipientCount === 1 ? "" : "s"}`}
                </Button>
              </Stack>
              {sending && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} color="success" />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <RecipientsPanel
              recipientMode={recipientMode}
              onRecipientModeChange={setRecipientMode}
              selectedCount={schoolRecipientCount}
              selectedSchoolsData={selectedSchoolsData}
              onOpenSelection={() => setSchoolSelectionDialogOpen(true)}
              phoneInputText={phoneInputText}
              onPhoneInputChange={setPhoneInputText}
              onPhoneFileImport={handlePhoneFileImport}
              phoneNumbers={phoneNumbers}
              phoneFileInputRef={phoneFileInputRef}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderColor: alpha(WA_GREEN, 0.2),
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => setTemplatesExpanded((v) => !v)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 2,
                border: 0,
                cursor: "pointer",
                bgcolor: WA_SURFACE_HEADER,
                "&:hover": { bgcolor: alpha(WA_GREEN, 0.16) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: "secondary.main",
                  }}
                >
                  <TemplateIcon />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Message templates
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {templates.length} template{templates.length === 1 ? "" : "s"} available
                  </Typography>
                </Box>
              </Box>
              {templatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={templatesExpanded}>
              <Box sx={{ p: 2.5, pt: 0, bgcolor: "#fff" }}>
                {loadingTemplates ? (
                  <LinearProgress sx={{ mt: 2 }} />
                ) : (
                  renderTemplateGrid(false)
                )}
              </Box>
            </Collapse>
          </Paper>
        </Box>
          </CardContent>
        </Card>

        <Dialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Choose a template</DialogTitle>
          <DialogContent dividers>
            {loadingTemplates ? (
              <LinearProgress sx={{ my: 2 }} />
            ) : (
              <Box sx={{ py: 1 }}>{renderTemplateGrid(true)}</Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setTemplateDialogOpen(false)} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Schedule message</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Date & time"
                type="datetime-local"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Alert severity="info" icon={<ScheduleIcon />}>
                Message will be queued for {recipientCount} recipient
                {recipientCount === 1 ? "" : "s"} at the scheduled time.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setScheduleDialogOpen(false)} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleScheduleMessage}
              sx={{ ...WHATSAPP_GRADIENT_SX, textTransform: "none" }}
            >
              Schedule
            </Button>
          </DialogActions>
        </Dialog>

        <MdlSelectMultipleSchool
          open={schoolSelectionDialogOpen}
          onClose={() => setSchoolSelectionDialogOpen(false)}
          selectedSchools={selectedSchools}
          onSelectChange={handleSchoolSelectChange}
          title="Select Schools"
        />
      </motion.div>
    </Box>
  );
};

export default WhatsAppMessaging;
